import {
  HomeSlideRepository,
  CreateHomeSlideInput,
  UpdateHomeSlideInput,
} from '../../domain/repositories/home-slide.repository';
import { HomeSlideEntity, HomeSlideProps } from '../../domain/entities/home-slide.entity';
import { IHttpClient } from '../../application/interfaces/http-client.interface';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export class HttpHomeSlideRepository implements HomeSlideRepository {
  constructor(private readonly httpClient: IHttpClient) {}

  public async getSlides(activeOnly: boolean = false): Promise<HomeSlideEntity[]> {
    const endpoint = activeOnly ? '/home-slides/public' : '/home-slides';
    const res = await this.httpClient.get<ApiResponse<HomeSlideProps[]>>(endpoint);

    if (!res.success || !res.data) {
      return [];
    }

    return res.data.map((item) => new HomeSlideEntity(item));
  }

  public async getSlideById(id: string): Promise<HomeSlideEntity | null> {
    const res = await this.httpClient.get<ApiResponse<HomeSlideProps>>(`/home-slides/${id}`);
    if (!res.success || !res.data) {
      return null;
    }
    return new HomeSlideEntity(res.data);
  }

  public async createSlide(data: CreateHomeSlideInput): Promise<HomeSlideEntity> {
    const res = await this.httpClient.post<ApiResponse<HomeSlideProps>>('/home-slides', data);
    if (!res.success || !res.data) {
      throw new Error(res.message || 'Error al crear el banner publicitario');
    }
    return new HomeSlideEntity(res.data);
  }

  public async updateSlide(id: string, data: UpdateHomeSlideInput): Promise<HomeSlideEntity> {
    const res = await this.httpClient.put<ApiResponse<HomeSlideProps>>(`/home-slides/${id}`, data);
    if (!res.success || !res.data) {
      throw new Error(res.message || 'Error al actualizar el banner publicitario');
    }
    return new HomeSlideEntity(res.data);
  }

  public async toggleSlideStatus(id: string, isActive: boolean): Promise<HomeSlideEntity> {
    const res = await this.httpClient.patch<ApiResponse<HomeSlideProps>>(`/home-slides/${id}/status`, {
      isActive,
    });
    if (!res.success || !res.data) {
      throw new Error(res.message || 'Error al cambiar estado del banner');
    }
    return new HomeSlideEntity(res.data);
  }

  public async reorderSlides(items: { id: string; displayOrder: number }[]): Promise<void> {
    const res = await this.httpClient.patch<ApiResponse<void>>('/home-slides/reorder', { items });
    if (!res.success) {
      throw new Error(res.message || 'Error al reordenar los banners');
    }
  }

  public async deleteSlide(id: string): Promise<void> {
    const res = await this.httpClient.delete<ApiResponse<void>>(`/home-slides/${id}`);
    if (!res.success) {
      throw new Error(res.message || 'Error al eliminar el banner');
    }
  }
}
